import { HttpParams } from '@angular/common/http';

// Wire-facing keys are snake_case (.claude/docs/api-layer.md). A literal key
// with an uppercase letter fails to compile; dynamic string keys pass unchecked.
// prettier-ignore
type UppercaseLetter =
  | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M'
  | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X' | 'Y' | 'Z';

type WireCased<T> = {
  [K in keyof T]: K extends `${string}${UppercaseLetter}${string}`
    ? never
    : T[K];
};

export type QueryPrimitive = string | number | boolean;
export type QueryValue =
  | QueryPrimitive
  | ReadonlyArray<QueryPrimitive>
  | null
  | undefined;
export type QueryParams = Record<string, QueryValue>;

/**
 * GET **query strings** only - form-urlencoded bodies go through `toFormBody`.
 * Flat: skips null/undefined, appends arrays as repeated bare `key=value`, so
 * a PHP-array param must spell its own brackets (`'company_category_ids[]'`).
 */
export const toQueryParams = <T extends QueryParams>(
  query: T & WireCased<T>,
): HttpParams => {
  let params = new HttpParams();
  const source: QueryParams = query;

  for (const [key, value] of Object.entries(source)) {
    if (value === null || value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        params = params.append(key, String(item));
      }
      continue;
    }

    params = params.set(key, String(value));
  }

  return params;
};

export type FormBodyPrimitive =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined;

type DeepWireCasedValue<V> = V extends FormBodyPrimitive
  ? V
  : V extends ReadonlyArray<infer E>
    ? ReadonlyArray<DeepWireCasedValue<E>>
    : V extends object
      ? DeepWireCased<V>
      : V;

// WireCased, but recursing into nested objects and arrays - the backend reads
// nested form-body keys verbatim, so casing there is a hard wire requirement.
type DeepWireCased<T> = {
  [K in keyof T]: K extends `${string}${UppercaseLetter}${string}`
    ? never
    : DeepWireCasedValue<T[K]>;
};

/**
 * Form-urlencoded **request bodies** (PUT/PATCH with `HEADERS.formUrlEncoded`)
 * - query strings go through `toQueryParams`. Recursive: objects nest as
 * `key[sub]`, arrays as `key[]=value`, and null/undefined become `''` instead
 * of being dropped. All of that is load-bearing wire behavior - don't change.
 */
export const toFormBody = <T extends object>(
  body: T & DeepWireCased<T>,
): HttpParams => {
  let params = new HttpParams();

  const isPrimitive = (v: any) =>
    ['string', 'number', 'boolean'].includes(typeof v) ||
    v instanceof Date ||
    v == null;

  const appendParams = (value: any, prefix = ''): void => {
    if (value == null) {
      if (prefix) params = params.append(prefix, '');
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (isPrimitive(item) || item == null) {
          params = params.append(`${prefix}[]`, item ?? '');
        } else {
          appendParams(item, `${prefix}[${index}]`);
        }
      });
      return;
    }

    if (typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        const v = value[key];
        const keyPath = prefix ? `${prefix}[${key}]` : key;
        if (Array.isArray(v)) appendParams(v, keyPath);
        else if (isPrimitive(v)) params = params.append(keyPath, v ?? '');
        else appendParams(v, keyPath);
      });
      return;
    }

    if (isPrimitive(value) && prefix)
      params = params.append(prefix, value ?? '');
  };

  appendParams(body);
  return params;
};

/**
 * Camelizes an object's top-level keys for the few endpoints whose wire
 * contract is camelCase (e.g. POST /company/categories) - the sanctioned
 * exemptions to the snake_case rule above. Shallow on purpose: only the
 * top-level keys are part of those contracts.
 */
export const toCamelCaseKeys = <T extends object>(
  obj: T,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key.replace(/_([a-z])/g, (g) => g[1].toUpperCase())] = value;
  }
  return result;
};
